import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const AUDIT_LOG_SEARCH_RESOURCE = "audit-log" as const;
export const AUDIT_LOG_SEARCH_OPERATION = "search" as const;
export const AUDIT_LOG_SEARCH_API_PATH = `/v1/${AUDIT_LOG_SEARCH_RESOURCE}/${AUDIT_LOG_SEARCH_OPERATION}` as const;

export type AuditLogSearchParameters = paths[typeof AUDIT_LOG_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type AuditLogSearchBody =
  paths[typeof AUDIT_LOG_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type AuditLogSearchResponse =
  paths[typeof AUDIT_LOG_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useAuditLogSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<AuditLogSearchParameters, AuditLogSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<AuditLogSearchResponse, Error, AuditLogSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<AuditLogSearchParameters, AuditLogSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      AUDIT_LOG_SEARCH_RESOURCE,
      AUDIT_LOG_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<AuditLogSearchResponse>(
        AUDIT_LOG_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
